import {
  AiBlockType,
  ComparisonValueType,
  ConditionComparator,
  ConditionLogic,
  IntegrationAuthApp,
} from '@prisma/client';
import { v4 as UUID } from 'uuid';

import { DYNAMIC_EDITOR_TAG_EXTENSION_NAME } from '@/components/dynamic-input/constants';
import { gmailOperationsDefinition } from '@/levity/integrations/registry/email/providers/gmail.definition';

import { AppNodeType, FlowNodeType, type FlowTemplate } from '../../types';

const IDs = {
  aiBlocks: {
    classifier: UUID(),
    extractor: UUID(),
  },
  nodes: {
    gmailTrigger: UUID(),
    classifier: UUID(),
    extractor: UUID(),
    pathQuoteRequest: UUID(),
    pathOther: UUID(),
  },
  metadata: {
    quoteRequest: UUID(),
    other: UUID(),
  },
};

const GMAIL_ICON = {
  alt: 'gmailV1 application icon',
  src: '/imgs/icons/gmailV1.svg',
};

export const identifyQuoteRequestsExtractDetailsTemplate: FlowTemplate = {
  name: 'identify-quote-requests-extract-details',
  description: 'Template for identifying quote requests via email and extracting relevant details.',
  integrations: [IntegrationAuthApp.gmailV1, IntegrationAuthApp.gsheetsV1],
  title: 'Identify quote requests & extract details',
  nodes: [
    {
      id: IDs.nodes.gmailTrigger,
      type: FlowNodeType.APP,
      icon: `/imgs/icons/gmailV1.svg`,
      metadata: {
        type: AppNodeType.TRIGGER,
        integration: IntegrationAuthApp.gmailV1,
        operationId: gmailOperationsDefinition.GetAllMessages.id,
      },
    },
    {
      id: IDs.nodes.classifier,
      type: FlowNodeType.AI_BLOCK,
      icon: `/imgs/icons/aiblocks/classify.svg`,
      metadata: {
        aiBlockId: IDs.aiBlocks.classifier,
      },
      defaultDynamicInputContent: [
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Subject',
              image: GMAIL_ICON,
            },
          },
        },
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Body',
              image: GMAIL_ICON,
            },
          },
        },
      ],
    },
    {
      id: IDs.nodes.extractor,
      type: FlowNodeType.AI_BLOCK,
      icon: `/imgs/icons/aiblocks/extract.svg`,
      metadata: {
        aiBlockId: IDs.aiBlocks.extractor,
      },
      defaultDynamicInputContent: [
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Subject',
              image: GMAIL_ICON,
            },
          },
        },
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Body',
              image: GMAIL_ICON,
            },
          },
        },
      ],
      dynamicOutputKeys: [
        {
          name: 'origin',
          code: 'origin',
          default: false,
          rawName: 'origin',
          provider: 'aiblocks/extract',
        },
        {
          name: 'destination',
          code: 'destination',
          default: false,
          rawName: 'destination',
          provider: 'aiblocks/extract',
        },
        {
          name: 'date',
          code: 'date',
          default: false,
          rawName: 'date',
          provider: 'aiblocks/extract',
        },
        {
          name: 'weight',
          code: 'weight',
          default: false,
          rawName: 'weight',
          provider: 'aiblocks/extract',
        },
        {
          name: 'commodity',
          code: 'commodity',
          default: false,
          rawName: 'commodity',
          provider: 'aiblocks/extract',
        },
      ],
    },
    {
      id: IDs.nodes.pathQuoteRequest,
      type: FlowNodeType.PATH,
      icon: `/imgs/icons/aiblocks/${AiBlockType.classify}.svg`,
      metadata: {},
      conditionGroups: [
        {
          logic: ConditionLogic.AND,
          conditions: [
            {
              logic: ConditionLogic.AND,
              aiBlockMetaDataId: null,
              comparator: ConditionComparator.EQUALS,
              comparisonValueType: ComparisonValueType.STRING,
              comparisonValue: 'quote request',
              sourceNodeId: IDs.nodes.classifier,
              sourceOutputKey: 'Classification Result',
            },
          ],
        },
      ],
    },
    {
      id: IDs.nodes.pathOther,
      type: FlowNodeType.PATH,
      icon: `/imgs/icons/aiblocks/${AiBlockType.classify}.svg`,
      metadata: {},
      conditionGroups: [
        {
          logic: ConditionLogic.AND,
          conditions: [
            {
              logic: ConditionLogic.AND,
              aiBlockMetaDataId: null,
              comparator: ConditionComparator.EQUALS,
              comparisonValueType: ComparisonValueType.STRING,
              comparisonValue: 'other',
              sourceNodeId: IDs.nodes.classifier,
              sourceOutputKey: 'Classification Result',
            },
          ],
        },
      ],
    },
  ],
  edges: [
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.gmailTrigger,
      targetId: IDs.nodes.classifier,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.classifier,
      targetId: IDs.nodes.pathQuoteRequest,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.classifier,
      targetId: IDs.nodes.pathOther,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.pathQuoteRequest,
      targetId: IDs.nodes.extractor,
    },
  ],
  aiBlocks: [
    {
      id: IDs.aiBlocks.classifier,
      name: 'Classifier',
      emoji: '🍉',
      type: 'classify',
      metadata: [
        {
          labelSource: 'UserAnnotated',
          key: 'quote request',
          id: UUID(),
          value: 'quote request',
          type: 'AiBlockLabel',
        },
        {
          labelSource: 'UserAnnotated',
          key: 'other',
          id: UUID(),
          value: 'other',
          type: 'AiBlockLabel',
        },
      ],
    },
    {
      id: IDs.aiBlocks.extractor,
      name: 'Extractor',
      emoji: '🍉',
      type: 'extract',
      metadata: [
        {
          labelSource: 'UserAnnotated',
          key: 'origin',
          id: UUID(),
          value: 'origin',
          type: 'AiBlockLabel',
        },
        {
          labelSource: 'UserAnnotated',
          key: 'destination',
          id: UUID(),
          value: 'destination',
          type: 'AiBlockLabel',
        },
        {
          labelSource: 'UserAnnotated',
          key: 'date',
          id: UUID(),
          value: 'date',
          type: 'AiBlockLabel',
        },
        {
          labelSource: 'UserAnnotated',
          key: 'weight',
          id: UUID(),
          value: 'weight',
          type: 'AiBlockLabel',
        },
        {
          labelSource: 'UserAnnotated',
          key: 'commodity',
          id: UUID(),
          value: 'commodity',
          type: 'AiBlockLabel',
        },
      ],
    },
  ],
};
